import Cookies from "js-cookie";
import config from "../config/config";
import * as UTILS from "../utils/utils";

// All spektrix API queries will be handled by this method
const Spektrix_Request = async (
  url,
  verb = "GET",
  body = null,
  parser,
  isCustomer = false
) => {
  if (!url) {
    throw new Error(`A URL must be provided to Spektrix_Request`);
  }

  let payload = {
    credentials: "include",
    headers: {
      "Content-type": "application/json; charset=UTF-8",
    },
    method: verb,
    body: body,
  };

  try {
    const response = await fetch(url, payload);

    if (!response.ok) {
      if (isCustomer) {
        // If customer call returns nothing remove the customer cookie
        Cookies.remove("spektrix_session");
      }
      const errorData = await response.json();
      const errorText = errorData?.problems?.join(". ");

      throw new Error(errorText ? errorText : errorData.message);
    }

    if (response.status != 204) {
      let data = null;
      if (parser) {
        const text = await response.text();
        data = parser(text);
      } else {
        data = await response.json();
      }

      return data;
    }

    if (response.status === 204) {
      return null;
    }
  } catch (error) {
    throw error;
  }
};

// Non spektrix API queries. These use ajax endpoints in the theme or spektrix plugin.
export const getEventImage = async (eventId) => {
  if (!eventId) return;

  let response = await fetch(config.AJAX_URL, {
    method: "POST",
    body: new URLSearchParams({ action: "event_image", event_id: eventId }),
  });
  let response_text = await response.text();
  return response_text;
};

// Spektrix API queries
export const getPlanData = async (planId) => {
  const id = typeof planId === "string" ? planId : config.PLAN_ID;

  let response = await Spektrix_Request(
    `${config.SPEKTRIX_PLUGIN_URL}/plans/${id}.json`,
    "GET"
  );
  return response;
};

export const getSeatData = async () => {
  // Make both requests in parallel
  const [proxyResponse, directResponse] = await Promise.all([
    fetch(`/?spektrix-detail&instance=${config.INSTANCE_ID}`, {
      method: "GET",
      credentials: "include",
      accept: "application/json",
      headers: {
        Origin: `${config.DOMAIN_NAME}`,
      },
    }).catch((err) => {
      console.error("Proxy request failed:", err);
      throw new Error("Proxy request failed");
    }),
    Spektrix_Request(
      `${config.SPEKTRIX_API_URL}/instances/${config.INSTANCE_ID}/status/detail?includeLockInformation=true&includeChildPlans=true&$expand=bands`,
      "GET",
      null,
      UTILS.parseSeatDetailObject
    ).catch((err) => {
      console.error("Direct request failed:", err);
      throw new Error("Direct request failed");
    }),
  ]).catch((err) => {
    console.error("Error in Promise.all:", err);
    throw new Error("Failed to fetch seat data");
  });

  // Process the responses
  const proxyText = await proxyResponse.text().catch((err) => {
    console.error("Failed to read proxy response text:", err);
    throw new Error("Failed to read proxy response text");
  });

  const proxyData = JSON.parse(proxyText);
  const maskedSeatIds = proxyData?.data?.response || {};

  const mergedData = directResponse.concat(maskedSeatIds);
  // console.log("Merged Data:", mergedData);

  return UTILS.parseSeatStatus(mergedData);
};

export const getAreaData = async (areaId) => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/instances/${config.INSTANCE_ID}/status/areas?areaIds=${areaId}&includeLockInformation=true&includeChildPlans=true&expand=2`,
    "GET"
  );

  return response;
};

export const addBestAvailable = async (quantity, priceBands = null) => {
  let body = {
    Quantity: quantity,
    Instance: config.INSTANCE_ID,
  };
  if (priceBands) {
    body.priceBands = priceBands;
  }

  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/tickets/best-available`,
    "POST",
    JSON.stringify(body)
  );

  return response;
};

export const getBestAvailableByBands = async (quantity, priceBands = null) => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/instances/${config.INSTANCE_ID}/best-available?quantity=${quantity}&bandIds=${priceBands}`,
    "GET"
  );

  return response;
};

export const addTickets = async (
  quantity = 1,
  ticketType = "",
  seat_id = "",
  instance_id = config.INSTANCE_ID,
  planId = config.PLAN_ID
) => {
  const body = {
    Instance: instance_id,
    Type: ticketType,
  };

  // Reserved tickets require a seat id to be passed in unreserved require the seating plan id
  if (seat_id) {
    body.Seat = seat_id;
  } else {
    body.SeatingPlan = planId;
  }

  // Add a data object to the body for each required ticket
  let body_arr = [];
  for (let i = 0; i < quantity; i++) {
    body_arr.push(body);
  }

  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/tickets`,
    "POST",
    JSON.stringify(body_arr)
  );
  return response;
};

export const removeTickets = async (ticketIds = "") => {
  if (!ticketIds) {
    throwError({
      ok: false,
      statusText: "removeTickets() requires ticketIds",
    });
  }

  let response = await Spektrix_Request(
    `${
      config.SPEKTRIX_API_URL
    }/basket/tickets?ticketIds=${ticketIds.join(",")}`,
    "DELETE"
  );
  return response;
};

export const patchTicket = async (ticketId = null, body = null) => {
  if (!ticketId) {
    throwError({
      ok: false,
      statusText: "patchTickets() requires ticketId",
    });
  }

  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/tickets/${ticketId}`,
    "PATCH",
    JSON.stringify(body)
  );
  return response;
};

export const getFunds = async () => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/funds`,
    "GET"
  );
  return response;
};

export const getTicketTypes = async () => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/ticket-types`,
    "GET"
  );
  return response;
};

export const getBands = async () => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/bands`,
    "GET"
  );
  return response;
};

export const getInstance = async (instanceId = config.INSTANCE_ID) => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/instances/${instanceId}?$expand=1`,
    "GET"
  );
  return response;
};

export const getInstanceStatus = async (instanceId = config.INSTANCE_ID) => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/instances/${instanceId}/status`,
    "GET"
  );
  return response;
};

export const getInstancePriceList = async (instanceId = config.INSTANCE_ID) => {
  let response = await fetch(`/?spektrix-prices&instance=${instanceId}`, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-type": "application/json; charset=UTF-8",
    },
  });
  let data = await response.json();

  // Add commission to price
  const priceListData = data.prices.map((price) => {
    price.customItems?.forEach((item) => {
      if (item.name === "Commission") {
        price.amount = parseFloat(price.amount) + parseFloat(item.amount);
      }
    });

    return price;
  });

  return priceListData;
};

export const getSupplementaryEvents = async (
  instanceId = config.INSTANCE_ID
) => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/instances/${instanceId}/supplementary-events?$expand=event,priceList`,
    "GET"
  );

  return response;
};

export const getEvent = async (eventId = config.EVENT.event_id) => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/events/${eventId}?$expand=1`,
    "GET"
  );
  return response;
};

export const getEventInstances = async (eventId = config.EVENT.event_id) => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/events/${eventId}/instances?interface=1`,
    "GET"
  );
  return response;
};

export const patchTicketType = async (ticketId, ticketType) => {
  if (!ticketId) {
    throwError({
      ok: false,
      statusText: "patchTicketType() requires ticketId",
    });
  }

  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/tickets/${ticketId}`,
    "PATCH",
    JSON.stringify({
      TicketType: ticketType,
    })
  );
  return response;
};

export const getBasket = async () => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket?$expand=tickets/instance,tickets/event,tickets/seat,tickets/ticketType,tickets/band,donations,membershipSubscriptions/membership,giftVouchers,merchandiseItems,customer/subscriptions,ticketDelivery,ticketDeliveryOptions,Offers`,
    "GET"
  );
  return response;
};

export const patchBasket = async (body) => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket`,
    "PATCH",
    JSON.stringify(body)
  );
  return response;
};

export const getBasketTickets = async () => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/tickets?$expand=1`,
    "GET"
  );
  return response;
};

export const clearBasket = async () => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/clear`,
    "GET"
  );
  return response;
};

export const getDonationPrompts = async () => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/donations-to-prompt`,
    "GET"
  );
  return response;
};

export const getCustomer = async () => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/customer?$expand=tags,subscriptions/membership,AllStatements`,
    "GET",
    null,
    null,
    true
  );

  // Must match structure in SpektrixSession.php
  const session_data = {
    customer: response.email ?? "",
    id: response.id ?? "",
    email: response.email ?? "",
    firstName: response.firstName ?? "",
    lastName: response.lastName ?? "",
    priority_events: [],
  };

  // If the script in the iframe dispatches the update_session event then reset the cookie and update the session
  Cookies.set("spektrix_session", JSON.stringify(session_data), {
    expires: 1 / 24,
    path: "/",
  });

  return response;
};

export const patchCustomerDetails = async (details) => {
  if (!details) return;
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/customer`,
    "PATCH",
    JSON.stringify(details)
  );
  return response;
};

export const getCustomerAddresses = async () => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/customer/addresses`,
    "GET"
  );
  return response;
};

export const patchCustomerAddresses = async (addresses) => {
  if (!addresses) return;
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/customer/addresses`,
    "PATCH"
  );
  return response;
};

export const logIn = async (body) => {
  if (!body) return;

  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/customer/authenticate`,
    "POST",
    JSON.stringify(body)
  );
  return response;
};

export const logOut = async () => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/customer/deauthenticate`,
    "POST"
  );
  // Clear the customer cached by the header <spektrix-login-status>, otherwise premium.js
  // reads the stale email on the next page load and recreates the PHP session
  try {
    localStorage.removeItem("spektrixCustomer");
  } catch (e) {}
  Cookies.remove("spektrix_customer");
  await setWordpressSession();
  return response;
};

// Sync the customer into the PHP session (core/ajax.php) that gates the digital library.
// Called with no email it clears the session.
export const setWordpressSession = async (email = "") => {
  await fetch(config.AJAX_URL, {
    method: "POST",
    credentials: "same-origin",
    body: new URLSearchParams({
      action: "spektrix_session",
      spektrix_customer: email,
    }),
  });
};

export const resetPassword = async (email) => {
  if (!email) {
    throw new Error(`No email was provided`);
  }

  let response = await Spektrix_Request(
    `/customer/forgot-password?emailAddress=${email}&domain=${config.DOMAIN_NAME.replace(/^https?:\/\//, "")}`,
    "POST"
  );
  return response;
};

export const getAddressesFromPostcode = async (postcode) => {
  if (!postcode) {
    throw new Error(`No postcode was provided`);
  }

  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/addresses?postcode=${postcode}`,
    "GET"
  );
  return response;
};

export const getAddressFromPostcodeId = async (id) => {
  if (!id) {
    throw new Error(`No id was provided`);
  }

  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/addresses/${id}`,
    "GET"
  );
  return response;
};

export const getCountries = async () => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/countries`,
    "GET"
  );
  return response;
};

export const getStatements = async () => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/statements`,
    "GET"
  );
  return response;
};

export const createCustomer = async (customer) => {
  if (!customer) {
    throw new Error(`No customer data was provided`);
  }

  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/customer/?domain=${config.DOMAIN_NAME}`,
    "POST",
    JSON.stringify(customer)
  );
  return response;
};

export const addContactPreference = async (body) => {
  if (!body) {
    throw new Error(`No body was provided`);
  }
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/customer/agreed-statements`,
    "POST",
    JSON.stringify(body)
  );
  return response;
};

export const removeContactPreference = async (statementId) => {
  if (!statementId) {
    throw new Error(`No statement ID was provided`);
  }

  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/customer/agreed-statements/${statementId}`,
    "DELETE"
  );
  return response;
};

export const replaceContactPreferences = async (body) => {
  if (!body) {
    throw new Error(`No body was provided`);
  }

  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/customer/agreed-statements`,
    "PUT",
    JSON.stringify(body)
  );
  return response;
};

export const addCustomerTag = async (body) => {
  if (!body) {
    throw new Error(`No body was provided to addCustomerTag()`);
  }
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/customer/tags`,
    "POST",
    JSON.stringify(body)
  );
  return response;
};

export const removeCustomerTag = async (tagId) => {
  if (!tagId) {
    throw new Error(`No statement ID was provided`);
  }

  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/customer/tags/${tagId}`,
    "DELETE"
  );
  return response;
};

export const addMembership = async (body) => {
  if (!body) {
    throw new Error(`No body was provided to addMembership()`);
  }
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/membership-subscriptions`,
    "POST",
    JSON.stringify(body)
  );
  return response;
};

export const getPotentialDiscount = async (membershipId) => {
  if (!membershipId) {
    throw new Error(`No membership id was provided`);
  }
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/potentialdiscount?membershipId=${membershipId}`,
    "GET"
  );
  return response;
};

export const addDonation = async (body) => {
  if (!body) {
    throw new Error(`No body was provided to addDonationToBasket()`);
  }
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/donations`,
    "POST",
    JSON.stringify(body)
  );
  return response;
};

export const removeDonation = async (donationId) => {
  if (!donationId) {
    throw new Error(`No donationId was provided`);
  }
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/donations/${donationId}`,
    "DELETE"
  );
  return response;
};

export const patchDonation = async (donationId, body = null) => {
  if (!donationId) {
    throw new Error(`No donationId was provided`);
  }
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/donations/${donationId}`,
    "PATCH",
    JSON.stringify(body)
  );
  return response;
};

export const getOrders = async () => {
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/customer/orders?$expand=donations/fund,refunds,membershipsubscriptions/membership,tickets/instance/event,tickets/ticketType,tickets/seat`,
    "GET"
  );
  return response;
};

export const getAuthenticatedOrders = async (customerId) => {
  let response = fetch(`/?spektrix-customer-orders&customer-id=${customerId}`);
  return response.then((res) => {
    if (!res.ok) {
      throw new Error("Failed to fetch authenticated orders");
    }
    return res.json();
  });
};

export const removeMembership = async (subscriptionId) => {
  if (!subscriptionId) {
    throw new Error(`No subscriptionId was provided`);
  }
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/membership-subscriptions/${subscriptionId}`,
    "DELETE"
  );
  return response;
};

export const patchMembership = async (subscriptionId, body) => {
  if (!subscriptionId) {
    throw new Error(`No subscriptionId was provided`);
  }
  if (!body) {
    throw new Error(`No body was provided`);
  }
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/membership-subscriptions/${subscriptionId}`,
    "PATCH",
    JSON.stringify(body)
  );
  return response;
};

export const removeGiftVoucher = async (giftVoucherId) => {
  if (!giftVoucherId) {
    throw new Error(`No giftVoucherId was provided`);
  }
  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/basket/gift-vouchers/${giftVoucherId}`,
    "DELETE"
  );
  return response;
};

export const getETicketsPDF = async (customer, instance) => {
  // Accept either objects or raw ids
  const customerId = typeof customer === "object" ? customer?.id : customer;
  const instanceId = typeof instance === "object" ? instance?.id : instance;

  if (!instanceId) {
    throw new Error("No instance provided for eTickets link generation");
  }
  if (!customerId) {
    throw new Error("No customer provided for eTickets link generation");
  }

  const url = `/?spektrix-customer-e-tickets&instance-id=${instanceId}&customer-id=${customerId}`;

  return fetch(url).then((res) => {
    if (!res.ok) {
      throw new Error("Failed to fetch eTickets link");
    }
    return res.blob();
  });
};


export const getUpdatedPlanImage = async (instanceId = config.INSTANCE_ID) => {
  if (!instanceId) {
    throw new Error("No instance provided for plan image retrieval");
  }

  let response = await Spektrix_Request(
    `${config.SPEKTRIX_API_URL}/instances/${instanceId}/plan`,
    "GET"
  );
    
//get backgroundImageUrl from response
    const backgroundImageUrl = response?.backgroundImageUrl;
    if (!backgroundImageUrl) {
        throw new Error("No backgroundImageUrl found in plan data");
    }

    return backgroundImageUrl;
};
    
